import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { COLORS } from '@lib/types';
import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';
import { AvatarFallbackIcon } from './AvatarFallbackIcon';

function colorFromText(text: string): ColorType {
  const lastChar = text.charAt(text.length - 1);
  const charCode = lastChar.toLowerCase().charCodeAt(0);
  const index = charCode % COLORS.length;
  return COLORS[index];
}

const avatarVariants = cva(
  [
    // Layout
    'relative inline-flex shrink-0',
  ],
  {
    variants: {
      size: {
        xs: 'h-6 w-6',
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-12 w-12',
        xl: 'h-14 w-14',
      },
      shape: {
        circle: 'rounded-full',
        square: '[&_*]:rounded-lg',
        squircle: '[&_*]:mask [&_*]:is-squircle [&_*]:rounded-none',
      },
    },
    defaultVariants: {
      size: 'md',
      shape: 'circle',
    },
  }
);

const avatarDisplayVariants = cva(
  [
    // Layout
    'flex h-full w-full items-center justify-center',
    // Typography
    'font-medium uppercase select-none',
  ],
  {
    variants: {
      variant: {
        filled: [],
        soft: [],
        'soft-bordered': ['border-2'],
      },
      shape: {
        circle: 'rounded-full',
        square: 'rounded-lg',
        squircle: 'mask is-squircle rounded-none',
      },
    },
    defaultVariants: {
      variant: 'filled',
      shape: 'circle',
    },
  }
);

const avatarImageVariants = cva(
  [
    // Layout
    'relative h-full w-full',
    // Background placeholder
    'before:absolute before:inset-0 before:rounded-[inherit]',
    'before:bg-surface-raised',
  ],
  {
    variants: {
      shape: {
        circle: 'rounded-full',
        square: 'rounded-lg',
        squircle: 'mask is-squircle rounded-none',
      },
    },
    defaultVariants: {
      shape: 'circle',
    },
  }
);

const colorVariants = {
  filled: 'bg-this-solid text-white',
  soft: 'bg-this-bg text-this-text',
  'soft-bordered': 'bg-this-bg text-this-text border-this-line',
};

const neutralVariants = {
  filled: 'bg-ui text-fg-muted',
  soft: 'bg-surface text-fg-muted',
  'soft-bordered': 'bg-surface text-fg-muted border-line',
};

type AvatarProps = {
  /** Image source URL */
  src?: string | null;
  /** Image srcSet attribute */
  srcSet?: string | null;
  /** Alt text for image */
  alt?: string;
  /** Image loading strategy */
  loading?: 'lazy' | 'eager';
  /** Additional props for the img element */
  imgProps?: ComponentPropsWithoutRef<'img'>;
  /** Name to generate initials from */
  name?: string;
  /** Color for initials background */
  color?: ColorType | 'auto';
  /** Indicator element (e.g., status dot) */
  indicator?: ReactNode;
} & VariantProps<typeof avatarVariants> &
  VariantProps<typeof avatarDisplayVariants> &
  Omit<ComponentPropsWithoutRef<'div'>, 'children'>;

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      src,
      srcSet,
      alt,
      loading = 'lazy',
      imgProps,
      name,
      color = 'neutral',
      variant = 'filled',
      shape = 'circle',
      size = 'md',
      style,
      indicator,
      ...rest
    },
    ref
  ) => {
    const chars =
      name
        ?.match(/\b(\w)/g)
        ?.slice(0, 2)
        .join('') || '';

    const resolvedColor: ColorType = color === 'auto' ? colorFromText(chars) : color;

    return (
      <div
        ref={ref}
        className={cn(avatarVariants({ size, shape }), className)}
        style={style}
        {...rest}
      >
        {src || srcSet ? (
          <img
            className={cn(avatarImageVariants({ shape }))}
            src={src ?? undefined}
            srcSet={srcSet ?? undefined}
            alt={alt || name || 'avatar'}
            loading={loading}
            {...imgProps}
          />
        ) : (
          <div
            className={cn(
              avatarDisplayVariants({ variant, shape }),
              resolvedColor !== 'neutral'
                ? [setThisClass(resolvedColor), colorVariants[variant ?? 'filled']]
                : neutralVariants[variant ?? 'filled']
            )}
          >
            {chars || <AvatarFallbackIcon className="h-3/5 w-3/5" />}
          </div>
        )}

        {indicator}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar, avatarVariants };
export type { AvatarProps };
